import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  TextInput,
  Image,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { organizationsApi } from '../../services/apiService';
import { OrganizationList } from '../../types';
import { COLORS, LABELS } from '../../constants/config';
import { Card, LoadingScreen, EmptyState, PageContainer, Navbar, Breadcrumb } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';
import { useTranslation } from 'react-i18next';

import { tokens } from '@design';
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

export default function OrganizationsScreen() {
  const navigation = useNavigation<NavigationProp>();
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const { selectedYear } = useYear();

  // Theme colors
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    searchBg: isDark ? colors.backgroundAlt : tokens.colors.raw.white,
    searchInput: colors.textPrimary,
    name: colors.textPrimary,
    responsible: colors.textMuted,
    iconBg: isDark ? colors.primaryLight : colors.primary + '15',
  };
  const [organizations, setOrganizations] = useState<OrganizationList[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const fetchOrganizations = useCallback(async () => {
    try {
      setError(null);
      const params: Record<string, any> = {};
      if (searchQuery) params.search = searchQuery;

      const response = await organizationsApi.list(params);
      setOrganizations(response.results);
    } catch (err: any) {
      setError(err.message || t('organizations.loadFailed'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Refetch when year changes
  useEffect(() => {
    fetchOrganizations();
  }, [selectedYear]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchOrganizations();
  };

  const renderItem = ({ item }: { item: OrganizationList }) => (
    <TouchableOpacity
      style={[styles.card, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder, borderWidth: tokens.spacing.absolute["px"] }]}
      onPress={() => navigation.navigate('OrganizationDetail', { id: item.id })}
    >
      <View style={styles.cardContent}>
        {item.logo ? (
          <Image source={{ uri: item.logo }} style={styles.logo} />
        ) : (
          <View style={[styles.logoPlaceholder, { backgroundColor: themeColors.iconBg }]}>
            <Ionicons name="business" size={32} color={colors.primary} />
          </View>
        )}
        <View style={styles.info}>
          <Text style={[styles.name, { color: themeColors.name }]}>{item.name}</Text>
          {item.responsible_user_name && (
            <Text style={[styles.responsible, { color: themeColors.responsible }]}>
              {t('organizations.responsible')}: {item.responsible_user_name}
            </Text>
          )}
        </View>
        <Ionicons name="chevron-back" size={24} color={colors.iconDefault} />
      </View>
    </TouchableOpacity>
  );

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <PageContainer>
      <Navbar />
      <Breadcrumb
        items={[
          { label: t('nav.home') },
          { label: t('nav.organizations') },
        ]}
      />
      {/* Search */}
      <View style={[styles.searchContainer, { backgroundColor: themeColors.searchBg, borderColor: colors.border }]}>
        <TextInput
          style={[styles.searchInput, { color: themeColors.searchInput }]}
          placeholder={LABELS.search}
          placeholderTextColor={colors.textMuted}
          value={searchQuery}
          onChangeText={setSearchQuery}
          onSubmitEditing={fetchOrganizations}
          textAlign="right"
        />
        <Ionicons name="search" size={20} color={colors.iconDefault} style={styles.searchIcon} />
      </View>

      {error ? (
        <EmptyState
          icon="alert-circle-outline"
          title={LABELS.error}
          message={error}
          actionLabel={LABELS.refresh}
          onAction={fetchOrganizations}
        />
      ) : organizations.length === 0 ? (
        <EmptyState
          icon="business-outline"
          title={LABELS.noData}
          message={t('organizations.noOrganizationsRegistered')}
        />
      ) : (
        <FlatList
          data={organizations}
          renderItem={renderItem}
          keyExtractor={(item) => item.id.toString()}
          contentContainerStyle={styles.list}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        />
      )}
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.white,
    margin: tokens.spacing.absolute["4"],
    borderRadius: tokens.radii.absolute["radius-12"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: COLORS.border,
  },
  searchInput: {
    flex: 1,
    paddingVertical: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph font size"],
    color: COLORS.text,
  },
  searchIcon: {
    marginStart: tokens.spacing.absolute["2"],
  },
  list: {
    padding: tokens.spacing.absolute["4"],
    paddingTop: tokens.spacing.absolute["0"],
  },
  card: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    marginBottom: tokens.spacing.absolute["3"],
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    elevation: 3,
    overflow: 'hidden',
  },
  cardContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: tokens.spacing.absolute["4"],
  },
  logo: {
    width: 56,
    height: 56,
    borderRadius: tokens.radii.absolute["radius-8"],
    marginStart: tokens.spacing.absolute["3"],
  },
  logoPlaceholder: {
    width: 56,
    height: 56,
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: COLORS.primary + '15',
    justifyContent: 'center',
    alignItems: 'center',
    marginStart: tokens.spacing.absolute["3"],
  },
  info: {
    flex: 1,
    alignItems: 'flex-end',
  },
  name: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontWeight: '600',
    color: COLORS.text,
  },
  responsible: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: COLORS.textMuted,
    marginTop: tokens.spacing.absolute["1"],
  },
});

const styles = getStyles(true);
